import type { AnchorPosition, CommandAnchor } from '@tunasong/schemas'
import { commandAnchorSchema } from '@tunasong/schemas'
import type { ZodType } from 'zod'
import { z } from 'zod'
import type { Tool } from '../types.js'

/**
 * Structural shape of a registered command, derived from
 * `@tunasong/plugin-lib`'s `AgentCommandSpec`. Defined here structurally so
 * `agent-tools` stays free of the plugin-lib dep (which pulls Plate/Slate
 * into the Lambda bundle). Production wires `getCommandSpec` from
 * plugin-lib; both types align on the fields read here.
 */
export interface RunEditorCommandSpec {
  commandId: string
  anchorPositions: AnchorPosition[]
  argsSchema: ZodType<unknown>
}

export interface RunEditorCommandDeps {
  /**
   * Dispatch an editor command — typically by creating a proposal carrying the
   * commandId, args, and anchor. Server-side: the default impl in
   * agent-server creates a proposal; client-injected impls can dispatch
   * directly when appropriate (rare).
   */
  runCommand: (input: {
    agentId: string
    entityId: string
    userId: string
    commandId: string
    args?: Record<string, unknown>
    anchor: CommandAnchor
    summary: string
  }) => Promise<{ dispatched: true }>
  /**
   * Look up a command spec by id. Injected so tests don't need a populated
   * registry; production wires `getCommandSpec` from `@tunasong/plugin-lib`.
   */
  lookupCommand: (commandId: string) => RunEditorCommandSpec | undefined
}

const runEditorCommandSchema = z.object({
  entityId: z.string().min(1).describe('The id of the entity the command should run on.'),
  commandId: z.string().min(1).describe('The editor command id, e.g., "chord.insert".'),
  args: z.record(z.string(), z.unknown()).describe('Command-specific arguments.').optional(),
  anchor: commandAnchorSchema.describe('Where in the entity tree the command should act.'),
  summary: z
    .string()
    .min(1)
    .describe('One-sentence human-readable summary, e.g., "Insert Am7 after line 3".'),
})

export const createRunEditorCommandTool = (
  deps: RunEditorCommandDeps
): Tool<z.infer<typeof runEditorCommandSchema>, { dispatched: true }> => ({
  name: 'run_editor_command',
  description:
    'Dispatch a registered editor command against an entity. Use for plugin-provided actions that already exist in the UI.',
  schema: runEditorCommandSchema,
  handler: async (args, ctx) => {
    const spec = deps.lookupCommand(args.commandId)
    if (!spec) {
      return { ok: false, error: `unknown command: ${args.commandId}` }
    }
    if (!spec.anchorPositions.includes(args.anchor.position)) {
      return {
        ok: false,
        error: `anchor.position '${args.anchor.position}' not allowed for ${args.commandId}; allowed: ${spec.anchorPositions.join(', ')}`,
      }
    }
    const argsParse = spec.argsSchema.safeParse(args.args ?? {})
    if (!argsParse.success) {
      return { ok: false, error: `args validation failed: ${argsParse.error.message}` }
    }
    const result = await deps.runCommand({
      agentId: ctx.agentId,
      entityId: args.entityId,
      userId: ctx.userId,
      commandId: args.commandId,
      args: argsParse.data as Record<string, unknown>,
      anchor: args.anchor,
      summary: args.summary,
    })
    return { ok: true, result }
  },
})
